import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { BulkOwnerFinderModal } from "@/components/BulkOwnerFinderModal";
import { PageHeader } from "@/components/PageHeader";
import { PlatformLogo } from "@/components/PlatformLogo";
import { Skeleton } from "@/components/Skeleton";
import { Sparkline } from "@/components/Sparkline";
import {
  FilterBar,
  MarketCard,
  TimeRangeDropdown,
  filtersToBody,
  hasAnyFilter,
  resolveTimeRange,
  type Filters,
  type MarketListing,
  type TimeRangeValue,
} from "@/components/MarketShared";

interface AgencyEntry {
  id: string;
  platform: "myhome" | "ss";
  external_id: string;
  name: string;
  logo_url: string | null;
  color_hex: string | null;
  user_type: string | null;
  active_listings: number;
  agents: number;
  avg_price_usd: number | null;
  top_neighborhoods: string[];
  last_seen_at: string;
}
interface DailyPoint {
  date: string;
  listings: number;
}
interface AgencyDetail {
  agency: AgencyEntry;
  timeseries_30d: DailyPoint[];
  deal_breakdown: Record<string, number>;
  neighborhood_breakdown: Record<string, number>;
}

interface ListingsPage {
  items: MarketListing[];
  total: number;
  limit: number;
  offset: number;
}

const LISTINGS_PAGE = 24;

export function CompetitionAgency() {
  const { agencyId } = useParams<{ agencyId: string }>();
  const [data, setData] = useState<AgencyDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [listings, setListings] = useState<MarketListing[] | null>(null);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [listingsOffset, setListingsOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Full Market-style filter set (m², rooms, neighborhoods, …). Held in
  // component state only — no URL sync — since the competition view is a
  // drill-in, not a shareable saved search.
  const [filters, setFilters] = useState<Filters>({});
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ mode: "all" });
  const [ownerFinderOpen, setOwnerFinderOpen] = useState(false);

  // Stats panel only depends on the agency, so it loads once per agencyId
  // and isn't re-fetched when the listings filter flips.
  useEffect(() => {
    if (!agencyId) return;
    setData(null);
    setErr(null);
    api<AgencyDetail>(`/api/stats/competition/${agencyId}`)
      .then(setData)
      .catch((e) =>
        setErr(e instanceof ApiError ? e.message : "Failed to load agency"),
      );
  }, [agencyId]);

  // Custom-range with both bounds empty is a no-op — treat it like "all".
  // Memo recompute is cheap; it's the listings useEffect deps key, so it
  // shouldn't fire while the user types one half of a custom range.
  const timeBody = useMemo(() => resolveTimeRange(timeRange), [timeRange]);

  // The MarketSearchIn body for this agency: scoped to the firm, plus the
  // Market filter set, plus the freshness window. ``sort`` rides alongside
  // (filtersToBody intentionally omits it). Reused by the listings fetch,
  // load-more, and the bulk owner-finder so all three see the same slice.
  const searchBody = useMemo(
    () => ({
      market_agency_id: agencyId,
      sort: filters.sort || "newest",
      ...filtersToBody(filters),
      ...timeBody,
    }),
    [agencyId, filters, timeBody],
  );
  // Stable string key so the listings useEffect dep array doesn't see a
  // new object identity on every render and refetch in a loop.
  const searchKey = useMemo(() => JSON.stringify(searchBody), [searchBody]);

  // Non-FilterBar extras handed to the bulk owner-finder as its base
  // body: scope to this firm + the freshness window. ``sort`` is dropped
  // — the scan re-ranks by owner-match score, so listing sort is moot —
  // and the FilterBar fields are owned live by the modal, so they're left
  // out here too (the modal layers ``filtersToBody`` on top).
  const ownerFinderBase = useMemo(
    () => ({ market_agency_id: agencyId, ...timeBody }),
    [agencyId, timeBody],
  );

  // Listings refetch on agency change OR when any listing filter flips.
  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;
    setListings(null);
    setListingsOffset(0);
    setListingsTotal(0);
    api<ListingsPage>("/api/market/search", {
      method: "POST",
      json: { ...searchBody, limit: LISTINGS_PAGE, offset: 0 },
    })
      .then((page) => {
        if (cancelled) return;
        setListings(page.items);
        setListingsTotal(page.total);
        setListingsOffset(page.items.length);
      })
      .catch(() => {
        if (cancelled) return;
        setListings([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId, searchKey]);

  const loadMore = async () => {
    if (!agencyId || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api<ListingsPage>("/api/market/search", {
        method: "POST",
        json: { ...searchBody, limit: LISTINGS_PAGE, offset: listingsOffset },
      });
      setListings((prev) => (prev ? [...prev, ...page.items] : page.items));
      setListingsOffset((o) => o + page.items.length);
    } finally {
      setLoadingMore(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {err}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
        <Skeleton className="h-32 mb-4" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const a = data.agency;
  const sparkValues = data.timeseries_30d.map((p) => p.listings);
  const totalThisMonth = sparkValues.reduce((s, v) => s + v, 0);
  const dealEntries = Object.entries(data.deal_breakdown).sort(
    (x, y) => y[1] - x[1],
  );
  const neighborhoodEntries = Object.entries(data.neighborhood_breakdown).sort(
    (x, y) => y[1] - x[1],
  );
  const maxNeighborhood = Math.max(
    1,
    ...neighborhoodEntries.map(([, c]) => c),
  );

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
      <PageHeader
        title={a.name}
        subtitle={
          <span className="flex items-center gap-2 text-xs">
            <PlatformLogo platform={a.platform} size="sm" />
            <span>External ID: {a.external_id}</span>
            {a.user_type && (
              <span className="text-slate-600 uppercase tracking-wide">
                · {a.user_type}
              </span>
            )}
          </span>
        }
        actions={
          <Link to="/analytics/competition" className="btn-secondary">
            ← Competition
          </Link>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Active listings" value={a.active_listings.toLocaleString()} />
        <Stat
          label="Agents under firm"
          value={a.agents.toLocaleString()}
          hint={
            a.platform === "myhome"
              ? "MyHome: each account is one person"
              : "Distinct contact phones under this firm"
          }
        />
        <Stat
          label="Average asking"
          value={
            a.avg_price_usd != null
              ? `$${Math.round(a.avg_price_usd).toLocaleString()}`
              : "—"
          }
          hint="USD across active listings"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">
                Posting cadence
              </div>
              <div className="text-sm text-slate-400 mt-0.5">
                New listings per day, last 30 days
              </div>
            </div>
            <div className="text-2xl font-semibold text-slate-100 tabular-nums">
              {totalThisMonth}
            </div>
          </div>
          {totalThisMonth === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">
              No new listings in this window — they may be coasting on
              older inventory.
            </div>
          ) : (
            <Sparkline data={sparkValues} />
          )}
        </div>
        <div className="card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Deal mix
          </div>
          {dealEntries.length === 0 ? (
            <div className="text-sm text-slate-500">No active listings.</div>
          ) : (
            <div className="space-y-2">
              {dealEntries.map(([deal, count]) => {
                const pct = a.active_listings
                  ? Math.round((count / a.active_listings) * 100)
                  : 0;
                return (
                  <div key={deal}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 capitalize">
                        {deal === "unknown" ? "Unknown" : deal.replace("_", " ")}
                      </span>
                      <span className="text-slate-500 tabular-nums">
                        {count}{" "}
                        <span className="text-slate-600 text-xs">({pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="card p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">
          Neighborhood spread
        </div>
        {neighborhoodEntries.length === 0 ? (
          <div className="text-sm text-slate-500">No active listings.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {neighborhoodEntries.slice(0, 30).map(([nh, count]) => (
              <div key={nh} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-slate-300 truncate">
                  {nh === "unknown" ? "Unknown" : nh.replace(/_/g, " ")}
                </span>
                <div className="w-32 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent-500 rounded-full"
                    style={{
                      width: `${(count / maxNeighborhood) * 100}%`,
                    }}
                  />
                </div>
                <span className="text-slate-500 tabular-nums w-10 text-right">
                  {count}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Listings
            </div>
            <div className="text-sm text-slate-400 mt-0.5">
              Currently active on the portal, newest first
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setOwnerFinderOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-500/10 border border-accent-500/40 hover:border-accent-500/60 text-accent-100 text-xs font-medium px-3 py-1.5 transition"
              title="For every listing this competitor posts in the filtered set, find the same property listed by its owner — then bulk-publish the owner versions across your team"
            >
              🎯 Find owners (bulk)
            </button>
            <TimeRangeDropdown value={timeRange} onChange={setTimeRange} />
            {listings && (
              <div className="text-xs text-slate-500 tabular-nums ml-1">
                {listings.length.toLocaleString()} of {listingsTotal.toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Full Market filter set — m², rooms, neighborhoods, condition…
            The bulk owner-finder consumes the exact same filtered slice. */}
        <div className="card p-3 mb-4">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            hideOwnerDirect
          />
        </div>
        {listings === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="card p-8 text-center text-sm text-slate-500">
            No active listings on the portal right now.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <MarketCard key={l.id} listing={l} />
              ))}
            </div>
            {listingsOffset < listingsTotal && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : `Load more (${(listingsTotal - listingsOffset).toLocaleString()} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {ownerFinderOpen && agencyId && (
        <BulkOwnerFinderModal
          // Route through the "market" source (not "agency") so the scan
          // honors the FilterBar selection — ``market_agency_id`` keeps it
          // scoped to this firm while every other Market filter narrows
          // the set.
          source={{
            kind: "market",
            initialFilters: filters,
            baseBody: ownerFinderBase,
            label: hasAnyFilter(filters) ? `${a.name} · filtered` : a.name,
          }}
          onClose={() => setOwnerFinderOpen(false)}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="card p-5">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
        {label}
      </div>
      <div className="text-[26px] font-semibold text-slate-100 tabular-nums leading-none">
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}
